import React, { useState, useEffect, useRef } from 'react';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 外側クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);
  // 構造化データ用のJSON-LD
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: window.location.origin,
      },
      ...items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: item.label,
        ...(item.href && { item: `${window.location.origin}${item.href}` }),
      })),
    ],
  };

  return (
    <>
      {/* 構造化データ */}
      <script
        data-testid="breadcrumb-json-ld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbStructuredData),
        }}
      />

      {/* パンくずリスト */}
      <nav data-testid="breadcrumb-nav" aria-label="パンくずリスト" className="mb-3 sm:mb-6">
        {/* デスクトップ表示 */}
        <ol className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
          <li>
            <Link to="/" className="flex items-center hover:text-green-600 transition-colors">
              <span className="ml-1">ホーム</span>
            </Link>
          </li>

          {items.map((item, index) => (
            <li key={index} className="flex items-center">
              <FaChevronRight className="mx-2 text-gray-400" size={12} />
              {item.href ? (
                <Link to={item.href} className="hover:text-green-600 transition-colors">
                  {item.label}
                </Link>
              ) : (
                <span className="text-gray-800 font-medium">{item.label}</span>
              )}
            </li>
          ))}
        </ol>

        {/* モバイル表示 */}
        <div className="sm:hidden py-1">
          {items.length > 1 ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-green-600 transition-colors py-1"
                aria-expanded={isDropdownOpen}
              >
                <span className="text-gray-400">•••</span>
                <span className="truncate max-w-[200px]">{items[items.length - 1].label}</span>
                <FaChevronDown
                  size={10}
                  className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-1 stone-texture-bg border border-gray-200 rounded-lg shadow-lg z-10 min-w-[200px]"
                  role="menu" // ここにrole="menu"を追加
                >
                  <Link
                    to="/"
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-green-600 transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <span className="ml-2">ホーム</span>
                  </Link>
                  {items.map((item, index) => (
                    <div key={index}>
                      {item.href ? (
                        <Link
                          to={item.href}
                          className="block px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-green-600 transition-colors"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <span className="block px-3 py-2 text-sm text-gray-800 font-medium bg-gray-50">
                          {item.label}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-sm text-gray-600 py-1">
              <Link
                to="/"
                className="flex items-center hover:text-green-600 transition-colors"
              ></Link>
              {items.length > 0 && (
                <>
                  <FaChevronRight className="text-gray-400" size={12} />
                  {items[0].href ? (
                    <Link
                      to={items[0].href}
                      className="hover:text-green-600 transition-colors truncate"
                    >
                      {items[0].label}
                    </Link>
                  ) : (
                    <span className="text-gray-800 font-medium truncate">{items[0].label}</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Breadcrumb;
